import { useEffect, useState } from 'react';
import FacilityModal from '../modal/FacilityModal';
import CtaBarButton from '../base/cta-bar-button';
import {
  brandColor,
  getClassTextForColor,
  getEditorColor,
  renderColor,
} from '../../util/ColorUtil';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';
import {
  getEditorColors,
  isItUndefined,
  modifyLinks,
} from '../../util/DataUtil';
import { getItemHeightPerRow } from '../../util/CSSUtil';
import { cleanHTML } from '../utils/htmlUtils';
import { useRouter } from 'next/router';
import LottieIcon from '../icons/LottieIcon';
import { useStaggeredRevealByTwo } from '../../hooks/useStaggeredRevealByTwo';
import PulsePingIcon from '../icons/PulsePingIcon';

const Icon = ({ data, lang, global, width }) => {
  const router = useRouter();
  const { asPath, locale } = router;
  library.add(fas);
  library.add(fal);
  const items =
    data?.field_image_title_subtitle_items?.font_awesome_title_subtitle_para ||
    [];
  const { ref: iconBlockRef, entered } = useStaggeredRevealByTwo({
    total: items.length,
    threshold: 0.1,
  });
  const LOTTIE_DRAW_MS = 1200;
  const TEXT_FADE_MS = 260;
  const [started, setStarted] = useState(false);
  const [batch, setBatch] = useState(-1);
  const [revealed, setRevealed] = useState(() => items.map(() => false));
  const [textVisible, setTextVisible] = useState(() => items.map(() => false));
  const [playKeys, setPlayKeys] = useState(() => items.map(() => 0));

  const revealedCount = Math.min(items.length, (batch + 1) * 2);
  const [marketoTrigger, setMarketoTrigger] = useState(false);
  const emphasizedTextColor = getEditorColors(
    isItUndefined(data?.field_title_color),
    isItUndefined(data?.field_subtitle_color)
  ).filter((x) => x)[0];
  const [titleHeight, setTitleHeight] = useState([]);

  useEffect(() => {
    setPlayKeys(items.map(() => 0));
  }, [items.length]);

  useEffect(() => {
    if (width >= 576) {
      setTitleHeight(getItemHeightPerRow('item-title-icon', 2));
    }
  }, [data]);

  useEffect(() => {
    setBatch(-1);
    setRevealed(items.map(() => false));
    setTextVisible(items.map(() => false));
    setPlayKeys(items.map(() => 0));
  }, [items.length]);

  useEffect(() => {
    setBatch(-1);
    setRevealed(items.map(() => false));
    setTextVisible(items.map(() => false));
    setPlayKeys(items.map(() => 0));

    if (entered) setBatch(0);
  }, [entered, items.length]);

  useEffect(() => {
    if (!entered) return;
    if (batch < 0) return;

    const i1 = batch * 2;
    const i2 = i1 + 1;
    if (i1 >= items.length) return;

    setRevealed((prev) => {
      const next = [...prev];
      next[i1] = true;
      if (i2 < next.length) next[i2] = true;
      return next;
    });

    const tText = setTimeout(() => {
      setTextVisible((prev) => {
        const next = [...prev];
        next[i1] = true;
        if (i2 < next.length) next[i2] = true;
        return next;
      });
    }, 120);

    setPlayKeys((prev) => {
      const next = [...prev];
      next[i1] = (next[i1] || 0) + 1;
      if (i2 < next.length) next[i2] = (next[i2] || 0) + 1;
      return next;
    });

    const PING_MS = 1050;
    const tNext = setTimeout(() => {
      setBatch((b) => b + 1);
    }, PING_MS + TEXT_FADE_MS);

    return () => {
      clearTimeout(tText);
      clearTimeout(tNext);
    };
  }, [batch, entered, items.length]);

  const renderItems = (items) => {
    return items.map((item, index) => {
      const hasLottie = item?.field_animated_icons?.length > 0;
      const iconKey = hasLottie ? item.field_animated_icons[0]?.value : null;

      const isVisible = !!revealed[index];
      const isRevealed = !!revealed[index];
      const isTextVisible = !!textVisible[index];
      const playKey = playKeys[index] || 0;

      const TEXT_MS = 720;
      const TEXT_DELAY = 140;

      const textStyle = {
        opacity: isTextVisible ? 1 : 0,
        visibility: isTextVisible ? 'visible' : 'hidden',
        transform: isTextVisible ? 'translateY(0)' : 'translateY(14px)',
        filter: isTextVisible ? 'blur(0px)' : 'blur(3px)',
        transition: isTextVisible
          ? `opacity ${TEXT_MS}ms cubic-bezier(0.16, 1, 0.3, 1),
       transform ${TEXT_MS}ms cubic-bezier(0.16, 1, 0.3, 1),
       filter ${TEXT_MS}ms cubic-bezier(0.16, 1, 0.3, 1),
       visibility 0ms`
          : `opacity ${TEXT_MS}ms cubic-bezier(0.16, 1, 0.3, 1),
       transform ${TEXT_MS}ms cubic-bezier(0.16, 1, 0.3, 1),
       filter ${TEXT_MS}ms cubic-bezier(0.16, 1, 0.3, 1),
       visibility 0ms linear ${TEXT_MS}ms`,
        transitionDelay: isTextVisible ? `${TEXT_DELAY}ms` : '0ms',
      };
      const subtitleStyle = {
        ...textStyle,
        transitionDelay: isTextVisible ? '220ms' : '0ms',
      };

      return (
        <div
          key={`icon-item-${iconKey || item?.field_title?.[0]?.value || index}`}
          className={`item ${isVisible ? 'is-visible' : ''}`}
        >
          <div
            style={{ width: 'fit-content' }}
            onMouseEnter={() => {
              if (!isRevealed) return;
              if (!iconKey) return;

              setPlayKeys((prev) => {
                const next = [...prev];
                next[index] = (next[index] || 0) + 1;
                return next;
              });
            }}
          >
            {item?.field_font_awesome_icons.length !== 0 &&
              !hasLottie &&
              item?.field_font_awesome_icons[0].style && (
                <FontAwesomeIcon
                  icon={[
                    item?.field_font_awesome_icons[0]?.style,
                    item?.field_font_awesome_icons[0]?.icon_name,
                  ]}
                  style={{
                    fill:
                      data?.field_icon_color && data?.field_icon_color[0]
                        ? brandColor(data?.field_icon_color[0]?.value)
                        : '',
                  }}
                />
              )}
            {hasLottie && isVisible && iconKey === 'circle' ? (
              <PulsePingIcon
                bgColor={brandColor(data?.field_icon_color?.[0]?.value)}
                playKey={playKey}
                index={index}
              />
            ) : null}

            {hasLottie ? (
              <LottieIcon
                iconKey={iconKey}
                color={brandColor(data?.field_icon_color?.[0]?.value)}
                size={60}
                loop={false}
                visible={isRevealed}
                playKey={playKey}
              />
            ) : null}
          </div>

          <span
            style={{
              ...(titleHeight[index] > 0
                ? { height: titleHeight[index] + 'px' }
                : {}),
              ...textStyle,
            }}
            dangerouslySetInnerHTML={{
              __html: cleanHTML(
                modifyLinks(item?.field_title_color_paragraph[0]?.value, locale)
              ),
            }}
            className="item-title-icon"
          />

          <span
            style={textStyle}
            dangerouslySetInnerHTML={{
              __html: cleanHTML(
                modifyLinks(
                  item?.field_subtitle_color_paragraph[0]?.value,
                  locale
                )
              ),
            }}
            className="item-subtitle"
          />
        </div>
      );
    });
  };

  return (
    <div
      style={{
        backgroundColor: brandColor(data?.field_background_color[0]?.value),
      }}
      className={
        'icon ' +
        getClassTextForColor(data?.field_background_color[0]?.value) +
        (entered ? ' is-entered' : '')
      }
    >
      <div className={'inner-container'}>
        <div className={'top'}>
          <div
            className={'left'}
            style={{
              marginBottom:
                typeof data?.field_subtitle_color[0]?.value !== 'undefined' &&
                data?.field_cta_bar_button_selection[0]?.cta_bar_button
                  ? ''
                  : '0px',
            }}
          >
            <span
              className={`title ${emphasizedTextColor ? emphasizedTextColor : ''}`}
              dangerouslySetInnerHTML={{
                __html: cleanHTML(
                  modifyLinks(data?.field_title_color[0]?.value)
                ),
              }}
            />
            <span
              className={`subtitle ${emphasizedTextColor ? emphasizedTextColor : ''}`}
              dangerouslySetInnerHTML={{
                __html: cleanHTML(
                  modifyLinks(data?.field_subtitle_color[0]?.value)
                ),
              }}
            />
          </div>
          {data?.field_cta_bar_button_selection[0]?.cta_bar_button && (
            <CtaBarButton
              blockTypeClass={'icon'}
              pathname={true}
              bgColor={renderColor(
                getEditorColor(
                  brandColor(data?.field_background_color[0]?.value)
                )
              )}
              setMarketoTrigger={setMarketoTrigger}
              data={data?.field_cta_bar_button_selection[0]?.cta_bar_button}
            />
          )}
          {marketoTrigger && (
            <FacilityModal
              lang={lang}
              global={global}
              setTrigger={setMarketoTrigger}
              trigger={marketoTrigger}
            >
              {data.field_marketo_blocks[0]?.marketo_form_facility_block_type}
            </FacilityModal>
          )}
        </div>
        <div
          className={'items-helper'}
          style={{
            marginTop: data?.field_cta_bar_button_selection[0]?.cta_bar_button
              ? width >= 576 && width < 992
                ? '68px'
                : ''
              : '',
          }}
        >
          <div className={'items'} ref={iconBlockRef}>
            {renderItems(items)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Icon;
